/**
 * Backbone-compatible types shared between the in-app event bus
 * and external workers.
 *
 * These mirror the interfaces defined in packages/core
 * (mycelium-event-backbone.service.ts) as portable Zod schemas
 * so workers and edge functions can validate events identically.
 *
 * @module backbone
 * @since 2026-03-02
 */

import { z } from 'zod'

// ── Event Domain ────────────────────────────────────────────────────────

/**
 * Backbone domain categories for event organization.
 *
 * This is the canonical list of domains used by the in-app event backbone.
 * For specialist / dot-namespaced domains used by external workers,
 * see {@link MyceliumDomains} in `domains/index.ts`.
 *
 * Naming alignment with `PermissionDomain` (packages/types/permissions.ts):
 * - Matching:  'sales' | 'calendar' | 'accounting' | 'crm' | 'ai'
 * - Event-only (no RBAC needed): 'notification' | 'gamification' | 'integration'
 *   | 'user' | 'team' | 'workload' | 'system' | 'webhook' | 'cortex'
 * - Permission-only (no events): 'admin' | 'tasks' | 'organizations'
 * - Name aliases: EventDomain 'task' = PermissionDomain 'tasks';
 *   EventDomain 'documents' = PermissionDomain 'documents'
 * - New here: 'community' (aligns with PermissionDomain 'community')
 * - 'box' — Upstash Box agent runtime lifecycle (per-user/specialist/session containers)
 * - 'notebook' — Cortex Notebooks lifecycle and grounding/knowledge-base events
 */
export const EventDomainSchema = z.enum([
  'task',
  'sales',
  'calendar',
  'notification',
  'gamification',
  'accounting',
  'integration',
  'user',
  'team',
  'workload',
  'ai',
  'system',
  'webhook',
  'crm',
  'documents',  // Renamed from 'document' to align with PermissionDomain 'documents'
  'cortex',
  'community',  // Added: aligns with PermissionDomain 'community'
  'box',        // Upstash Box runtime lifecycle (additive — schema-safe)
  'notebook',   // Cortex Notebooks: knowledge-base lifecycle + grounding events
  'memory',     // Federated unified_memory rollups + consolidation events
  'forms',      // Form engine publish + submission events
  'research',   // Deep research / specialist-research PGMQ events
  'messaging',  // Messenger connectors (Telegram, WhatsApp, etc.)
  'a2a',        // Agent2Agent protocol gateway — inbound tasks from external agents
  'ontology',   // Shared knowledge graph: concepts, relations, assertions
])
export type EventDomain = z.infer<typeof EventDomainSchema>

// ── Event Metadata ─────────────────────────────────────────────────────

export const EventMetadataSchema = z.object({
  /** Service that created this event */
  source: z.string(),
  /** Services that have processed this event */
  processedBy: z.array(z.string()).default([]),
  /** Context gathered during enrichment */
  context: z.record(z.unknown()).default({}),
  /** Tags for filtering and routing */
  tags: z.array(z.string()).default([]),
  /** Priority level (0-10, higher = more important) */
  priority: z.number().min(0).max(10).default(5),
  /** Deferred processing — event won't be processed before this timestamp (ms since epoch) */
  notBefore: z.number().optional(),
})
export type EventMetadata = z.infer<typeof EventMetadataSchema>

// ── Backbone Event (in-app shape) ───────────────────────────────

export const BackboneEventSchema = z.object({
  id: z.string(),
  timestamp: z.number(),
  domain: EventDomainSchema,
  type: z.string(),
  /**
   * Federated principal that produced this event.
   * Resolves via `mycelium_principals` (humans, agents, systems, integrations).
   * For human-produced events, `userId` is also populated as a derived field.
   * For non-human principals, `userId` is undefined.
   *
   * Tenant fields are `.nullish()`: the canonical wire format writes explicit
   * `null` for absent tenancy (`mycelium_route_event`'s `jsonb_build_object`
   * and `serializeEvent`'s `?? null`), so consumers must accept `null`,
   * `undefined`, or string. Strict `.optional()` caused SCHEMA_VALIDATION_FAILED
   * on every tenant-less event (system warns, health checks) → DLQ flood.
   */
  principalId: z.string().nullish(),
  userId: z.string().nullish(),
  orgId: z.string().nullish(),
  teamId: z.string().nullish(),
  payload: z.record(z.unknown()),
  metadata: EventMetadataSchema,
  /** Schema version (required, locked). Current: 1 (2026-05-05). */
  version: z.literal(1),
  /**
   * Natural idempotency key for exactly-once processing across queues and retries.
   * Transport-level concern: PGMQ messages carry this at the top level so the
   * worker can check `hasAlreadyProcessed()` before executing handlers.
   * Added 2026-07-04 — optional for backward compatibility.
   */
  dedup_key: z.string().optional(),
})
export type BackboneEvent = z.infer<typeof BackboneEventSchema>

// ── PGMQ Message Schema ─────────────────────────────────────────────────

/**
 * PGMQ messages ARE BackboneEvents. This alias makes the intent explicit
 * at publish/consume boundaries. The worker validates incoming queue
 * messages against this schema.
 *
 * @since 2026-07-04
 */
export const PGMQMessageSchema = BackboneEventSchema
export type PGMQMessage = z.infer<typeof PGMQMessageSchema>

// ── Priority Mapping ────────────────────────────────────────────────────

/**
 * String priority enum for publisher API ergonomics.
 * Mapped to numeric `metadata.priority` (0-10) at enqueue time.
 *
 * Numeric thresholds:
 *   - >= 7 → high_priority queue + immediate pg_net trigger
 *   - < 7  → normal queue + 60s pg_cron fallback
 */
export const PRIORITY_MAP = {
  low: 2,
  normal: 5,
  high: 8,
  critical: 10,
} as const

export type EventPriority = keyof typeof PRIORITY_MAP

/**
 * Numeric priority threshold for immediate pg_net wake-up.
 * Events with `metadata.priority >= HIGH_PRIORITY_THRESHOLD` trigger
 * `notify_brain_on_mycelium_event()` and route to `mycelium_high_priority`.
 */
export const HIGH_PRIORITY_THRESHOLD = 7

// ── PGMQ Message Builder ────────────────────────────────────────────────

/**
 * Publisher-friendly envelope shape.
 * `buildPGMQMessage()` transforms this into a `PGMQMessage` (BackboneEvent).
 */
export interface PGMQEnvelope {
  domain: string
  type: string
  priority: EventPriority
  payload: Record<string, unknown>
  source: string
  userId?: string
  orgId?: string
  teamId?: string
  principalId?: string
  deduplicationKey?: string
}

/**
 * Build a `PGMQMessage` (BackboneEvent-shaped) from a publisher envelope.
 *
 * This is the canonical transformation from the publisher-friendly
 * `PGMQEnvelope` to the wire-format `PGMQMessage` that the worker validates
 * and processes.
 *
 * - `priority` (string) → `metadata.priority` (number via PRIORITY_MAP)
 * - `source` → `metadata.source`
 * - `deduplicationKey` → `dedup_key` (top-level for worker idempotency)
 * - `id` is a generated UUID (via `globalThis.crypto.randomUUID()`)
 * - `timestamp` is `Date.now()`
 * - `version` is locked to 1
 *
 * @since 2026-07-04
 */
export function buildPGMQMessage(envelope: PGMQEnvelope): PGMQMessage {
  const numericPriority = PRIORITY_MAP[envelope.priority] ?? PRIORITY_MAP.normal
  return {
    id: globalThis.crypto.randomUUID(),
    timestamp: Date.now(),
    domain: envelope.domain as EventDomain,
    type: envelope.type,
    principalId: envelope.principalId ?? envelope.userId,
    userId: envelope.userId,
    orgId: envelope.orgId,
    teamId: envelope.teamId,
    payload: {
      ...envelope.payload,
      // Keep source in payload for backward compatibility with existing consumers
      source: envelope.source,
      ...(envelope.deduplicationKey
        ? { deduplicationKey: envelope.deduplicationKey }
        : {}),
    },
    metadata: {
      source: envelope.source,
      priority: numericPriority,
      processedBy: [],
      context: {},
      tags: [],
    },
    version: 1,
    dedup_key: envelope.deduplicationKey,
  }
}

/**
 * Resolve which PGMQ queue to use based on string priority.
 * >= HIGH_PRIORITY_THRESHOLD (7) → 'mycelium_high_priority'
 * < HIGH_PRIORITY_THRESHOLD      → 'mycelium_events'
 */
export function resolveQueue(priority: EventPriority): string {
  const numeric = PRIORITY_MAP[priority] ?? PRIORITY_MAP.normal
  return numeric >= HIGH_PRIORITY_THRESHOLD
    ? 'mycelium_high_priority'
    : 'mycelium_events'
}

/**
 * In-app Mycelium event — alias for BackboneEvent.
 *
 * Matches the `MyceliumEvent` interface used throughout `packages/core`.
 * Import this type (or BackboneEvent) instead of defining a local interface.
 */
export type MyceliumEvent = BackboneEvent

// ── Dead Letter Entry ──────────────────────────────────────────────────

export const DeadLetterEntrySchema = z.object({
  /** Original event that failed */
  event: BackboneEventSchema,
  /** Error message from the last attempt */
  error: z.string(),
  /** Subscriber that failed */
  subscriberName: z.string(),
  /** Total attempts made */
  attempts: z.number().int().nonnegative(),
  /** Timestamp of first failure */
  firstFailedAt: z.number(),
  /** Timestamp of most recent failure */
  lastFailedAt: z.number(),
})
export type DeadLetterEntry = z.infer<typeof DeadLetterEntrySchema>

// ── Circuit Breaker State ──────────────────────────────────────────────

export const CircuitBreakerState = z.enum(['closed', 'open', 'half-open'])
export type CircuitBreakerState = z.infer<typeof CircuitBreakerState>

export const CircuitBreakerConfigSchema = z.object({
  /** Number of consecutive failures before the breaker opens */
  failureThreshold: z.number().int().positive().default(5),
  /** Duration in ms that the breaker stays open before testing */
  resetTimeoutMs: z.number().int().positive().default(30_000),
  /** Number of successes in half-open state to close the breaker */
  halfOpenSuccessThreshold: z.number().int().positive().default(2),
})
export type CircuitBreakerConfig = z.infer<typeof CircuitBreakerConfigSchema>

// ── Rate Limiter Config ────────────────────────────────────────────────

export const RateLimiterConfigSchema = z.object({
  /** Maximum events processed per window */
  maxEventsPerWindow: z.number().int().positive().default(100),
  /** Window duration in ms */
  windowMs: z.number().int().positive().default(1_000),
})
export type RateLimiterConfig = z.infer<typeof RateLimiterConfigSchema>
